import { useEffect, useRef } from 'react';

/**
 * Repeats `callback` every `delay` ms, **only** while the page is visible (not in a background tab).
 * Clears itself on unmount.
 */
export const useVisibilityInterval = (callback: () => void, delay: number) => {
  const savedCallback = useRef<() => void>();

  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay == null) return;

    let id: NodeJS.Timeout | null = null;

    const tick = () => {
      savedCallback.current?.();
    };

    const start = () => {
      if (id === null) {
        id = setInterval(tick, delay);
      }
    };

    const stop = () => {
      if (id !== null) {
        clearInterval(id);
        id = null;
      }
    };

    // Start immediately if page already visible
    if (document.visibilityState === 'visible') {
      id = setInterval(tick, delay);
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        start();
      } else {
        stop();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [delay]);
}; 